import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useBlockGroupContext } from "components/form/Blocks";

import { useSaveActionsContext } from "context/save";

import useSectionVideoApi from "hooks/api/private/blocks/useSectionVideoApi";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import { SectionVideoBody, SectionVideoDetails } from "types/api/blocks/sectionVideo";
import { BlockEnum } from "types/enums";

export type SectionVideoForm = {
    id: string | undefined;
    video: string;
    video_mobile: string;
};

type SectionVideoType = {
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionVideoForm>;
};

function useSectionVideo(): SectionVideoType {
    const { blockGroupCompositionId, blockId, graphicalOrder, blockGroupId } =
        useBlockGroupContext();

    const { saveSectionVideo, getOneSectionVideo, deleteSectionVideo } = useSectionVideoApi();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                video: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                video_mobile: {
                    type: "simple",
                    validators: blockId === BlockEnum.SectionVideoFull ? [notEmptyValidator] : [],
                },
            },
        };
    }, [blockId]);

    const initialValues: SectionVideoForm = React.useMemo(() => {
        return {
            id: undefined,
            video: "",
            video_mobile: "",
        };
    }, []);

    const formHook = useForm<SectionVideoForm>(schema, initialValues);

    const { showAllError, setForm, isValid, form } = formHook;

    const [sectionOneTitleDetails, setSectionOneTitleDetails] = React.useState<
        SectionVideoDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionVideo(
            { id: blockGroupCompositionId },
            { rejectNotFound: true },
        );

        apiCall.then(setSectionOneTitleDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionOneTitleDetails, getOneSectionVideo, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionOneTitleDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionOneTitleDetails.id;
                newForm.video = sectionOneTitleDetails.video;
                newForm.video_mobile = sectionOneTitleDetails.video_mobile;

                return newForm;
            });
        }
    }, [setForm, sectionOneTitleDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const sectionOneTitleBody: SectionVideoBody = {
                    id: blockGroupCompositionId,
                    video: form.video,
                    video_mobile: form.video_mobile,
                };

                const [apiCall] = saveSectionVideo(sectionOneTitleBody);

                const promises: Promise<unknown>[] = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionVideo,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    return React.useMemo(
        (): SectionVideoType => ({
            buttonDelete: deleteSectionVideo,
            formHook,
        }),
        [deleteSectionVideo, formHook],
    );
}

export default useSectionVideo;
